import React from "react";
import { Link, Route } from "react-router-dom";

import NotFound from "../components/NotFound";
import RequireAuth from "../components/RequireAuth";
import { AUTH_LEVELS } from "../lib/globals";
import COI, { loader as COILoader } from "../pages/TournamentManagement/COI";
import COIApproval, {
    loader as COIApprovalLoader,
} from "../pages/TournamentManagement/COIApproval";
import Field, {
    loader as FieldLoader,
} from "../pages/TournamentManagement/Field";
import FieldsIndex, {
    loader as FieldsIndexLoader,
} from "../pages/TournamentManagement/FieldsIndex";
import GameLogs, {
    loader as GameLogsLoader,
} from "../pages/TournamentManagement/GameLogs";
import HomeLeagues, {
    loader as HomeLeaguesLoader,
} from "../pages/TournamentManagement/HomeLeagues";
import TournamentManagementIndex, {
    loader as TournamentManagementIndexLoader,
} from "../pages/TournamentManagement/Index";
import TournamentManagementLayout from "../pages/TournamentManagement/Layout";
import Organization, {
    loader as OrganizationLoader,
} from "../pages/TournamentManagement/Organization";
import OrganizationIndex, {
    loader as OrganizationsIndexLoader,
} from "../pages/TournamentManagement/OrganizationsIndex";
import Product, {
    loader as ProductLoader,
} from "../pages/TournamentManagement/Product";
import ProductsIndex, {
    loader as ProductsLoader,
} from "../pages/TournamentManagement/Products";
import PromoCode, {
    loader as PromoCodeLoader,
} from "../pages/TournamentManagement/PromoCode";
import PromoCodes, {
    loader as PromoCodesLoader,
} from "../pages/TournamentManagement/PromoCodes";
import TeamsByTournament, {
    loader as TeamsByTournamentLoader,
} from "../pages/TournamentManagement/TeamsByTournament";
import Template, {
    loader as TemplateLoader,
} from "../pages/TournamentManagement/Template";
import TemplatesIndex, {
    loader as TemplatesIndexLoader,
} from "../pages/TournamentManagement/TemplatesIndex";
import TournamentCalendar, {
    loader as TournamentCalendarLoader,
} from "../pages/TournamentManagement/TournamentCalendar";
import TournamentPlayoffSchedule, {
    loader as TournamentPlayoffScheduleLoader,
} from "../pages/TournamentManagement/TournamentPlayoffSchedule";
import TournamentPoolSchedule, {
    loader as TournamentPoolScheduleLoader,
} from "../pages/TournamentManagement/TournamentPoolSchedule";
import TournamentManagementTournamentStandings, {
    loader as TournamentManagementTournamentStandingsLoader,
} from "../pages/TournamentManagement/TournamentStandings";
import UmpireSchedule, {
    loader as UmpireScheduleLoader,
} from "../pages/TournamentManagement/UmpireSchedule";
import Order, {
    loader as OrderLoader,
} from "../pages/TournamentManagement/components/Order";
import ScheduleActions from "../pages/TournamentManagement/components/ScheduleActions";
import ScheduleActionsLoader from "../pages/TournamentManagement/components/ScheduleActions/loader";
import Tournament from "../pages/TournamentManagement/components/Tournament";
import TournamentLoader from "../pages/TournamentManagement/components/Tournament/loaders/TournamentLoader";
import TournamentRegistrations from "../pages/TournamentManagement/components/TournamentRegistrations";
import { TournamentRegistrationsLoader } from "../pages/TournamentManagement/components/TournamentRegistrations/loaders/tournamentRegistrationsLoader";
import PlayoffBracket, {
    loader as PlayoffBracketLoader,
} from "../pages/Tournaments/Bracket";
import { IOrder, IProduct, IPromoCode } from "../types/api";

export default (
    <Route
        element={
            <RequireAuth allowedUserLevels={AUTH_LEVELS.tourneymanagement} />
        }
    >
        <Route
            path="tournament-management"
            handle={{
                crumb: () => (
                    <Link to="/tournament-management">
                        Tournament Management
                    </Link>
                ),
            }}
            element={<TournamentManagementLayout />}
        >
            <Route
                index
                element={<TournamentManagementIndex />}
                loader={TournamentManagementIndexLoader}
                handle={{
                    header: () => "Tournament Management",
                }}
            />
            <Route path="tournament">
                <Route
                    path=":tournamentId"
                    loader={TournamentManagementIndexLoader}
                >
                    <Route
                        path="edit"
                        element={<Tournament />}
                        loader={TournamentLoader}
                        shouldRevalidate={() => true}
                        handle={{
                            crumb: ({
                                tournamentId,
                            }: {
                                tournamentId: string;
                            }) => {
                                return (
                                    <Link
                                        to={`/tournament-management/tournament/edit/${tournamentId}`}
                                    >
                                        Edit Tournament
                                    </Link>
                                );
                            },
                            header: ({ header }: { header: string }) => header,
                        }}
                    />
                    <Route
                        path="calendar"
                        element={<TournamentCalendar />}
                        loader={TournamentCalendarLoader}
                        shouldRevalidate={() => true}
                        handle={{
                            crumb: ({
                                tournamentId,
                            }: {
                                tournamentId: string;
                            }) => {
                                return (
                                    <Link
                                        to={`/tournament-management/tournament/${tournamentId}/calendar`}
                                    >
                                        Tournament Calendar
                                    </Link>
                                );
                            },
                            header: ({ header }: { header: string }) => header,
                        }}
                    />
                    <Route
                        path="umpires"
                        element={<UmpireSchedule />}
                        loader={UmpireScheduleLoader}
                        shouldRevalidate={() => true}
                        handle={{
                            crumb: ({
                                tournamentId,
                            }: {
                                tournamentId: string;
                            }) => {
                                return (
                                    <Link
                                        to={`/tournament-management/tournament/${tournamentId}/umpires`}
                                    >
                                        Umpire Schedule
                                    </Link>
                                );
                            },
                            header: ({ header }: { header: string }) => header,
                        }}
                    />
                    <Route
                        path="schedule"
                        handle={{
                            crumb: () => "Tournament Schedule",
                            header: () => "Tournament Schedule",
                        }}
                    >
                        <Route
                            path="pool"
                            element={<TournamentPoolSchedule />}
                            loader={TournamentPoolScheduleLoader}
                            shouldRevalidate={() => true}
                            handle={{
                                crumb: ({
                                    tournamentId,
                                }: {
                                    tournamentId: string;
                                }) => {
                                    return (
                                        <Link
                                            to={`/tournament-management/tournament/${tournamentId}/schedule/pool`}
                                        >
                                            Pool Schedule
                                        </Link>
                                    );
                                },
                                header: ({ header }: { header: string }) =>
                                    header,
                            }}
                        />
                        <Route
                            path="bracket"
                            element={<TournamentPlayoffSchedule />}
                            loader={TournamentPlayoffScheduleLoader}
                            shouldRevalidate={() => true}
                            handle={{
                                crumb: ({
                                    tournamentId,
                                }: {
                                    tournamentId: string;
                                }) => {
                                    return (
                                        <Link
                                            to={`/tournament-management/tournament/${tournamentId}/schedule/bracket`}
                                        >
                                            Bracket Schedule
                                        </Link>
                                    );
                                },
                                header: ({ header }: { header: string }) =>
                                    header,
                            }}
                        />
                    </Route>
                    <Route
                        path="standings"
                        element={<TournamentManagementTournamentStandings />}
                        loader={TournamentManagementTournamentStandingsLoader}
                        shouldRevalidate={() => true}
                        handle={{
                            crumb: ({
                                tournamentId,
                            }: {
                                tournamentId: string;
                            }) => {
                                return (
                                    <Link
                                        to={`/tournament-management/tournament/${tournamentId}/standings`}
                                    >
                                        Tournament Standings
                                    </Link>
                                );
                            },
                            header: ({ header }: { header: string }) => header,
                        }}
                    />
                    <Route
                        path="playoffs"
                        element={<PlayoffBracket />}
                        loader={PlayoffBracketLoader}
                        shouldRevalidate={() => true}
                        handle={{
                            crumb: ({
                                tournamentId,
                            }: {
                                tournamentId: string;
                            }) => {
                                return (
                                    <Link
                                        to={`/tournament-management/tournament/${tournamentId}/standings`}
                                    >
                                        Tournament Bracket
                                    </Link>
                                );
                            },
                            header: ({ header }: { header: string }) => header,
                        }}
                    />
                    <Route
                        path="schedule-actions"
                        element={<ScheduleActions />}
                        loader={ScheduleActionsLoader}
                        shouldRevalidate={() => true}
                        handle={{
                            crumb: ({
                                tournamentId,
                            }: {
                                tournamentId: string;
                            }) => {
                                return (
                                    <Link
                                        to={`/tournament-management/tournament/${tournamentId}/schedule-actions`}
                                    >
                                        Schedule Actions
                                    </Link>
                                );
                            },
                            header: ({ header }: { header: string }) => header,
                        }}
                    />
                </Route>
                <Route
                    path="add"
                    element={<Tournament />}
                    loader={TournamentLoader}
                    shouldRevalidate={() => true}
                    handle={{
                        crumb: () => {
                            return (
                                <Link
                                    to={`/tournament-management/tournament/add`}
                                >
                                    Add Tournament
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="teams-by-tournament"
                element={<TeamsByTournament />}
                loader={TeamsByTournamentLoader}
                shouldRevalidate={() => true}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/teams-by-tournament`}>
                                Teams By Tournament
                            </Link>
                        );
                    },
                    header: () => "Teams By Tournament",
                }}
            />
            <Route
                path="templates"
                element={<TemplatesIndex />}
                loader={TemplatesIndexLoader}
                shouldRevalidate={() => true}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/tournament-management/templates`}>
                                Schedule Templates
                            </Link>
                        );
                    },
                    header: () => "Schedule Templates",
                }}
            >
                <Route
                    path="edit/:fieldId"
                    element={<Template />}
                    loader={TemplateLoader}
                    handle={{
                        crumb: ({ fieldId }: { fieldId: string }) => {
                            return (
                                <Link
                                    to={`/tournament-management/templates/edit/${fieldId}`}
                                >
                                    Edit Template
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="organizations"
                element={<OrganizationIndex />}
                loader={OrganizationsIndexLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/tournament-management/organizations`}>
                                Organizations
                            </Link>
                        );
                    },
                    header: ({ header }: { header: string }) => header,
                }}
            >
                <Route
                    path="edit/:orgId"
                    element={<Organization />}
                    loader={OrganizationLoader}
                    handle={{
                        crumb: ({ orgId }: { orgId: string }) => {
                            return (
                                <Link
                                    to={`/tournament-management/organizations/edit/${orgId}`}
                                >
                                    Edit Organization
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path="add"
                    element={<Organization />}
                    loader={OrganizationLoader}
                    handle={{
                        crumb: () => {
                            return (
                                <Link
                                    to={`/tournament-management/organizations/add`}
                                >
                                    Add Organization
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="fields"
                element={<FieldsIndex />}
                loader={FieldsIndexLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/tournament-management/fields`}>
                                Field List
                            </Link>
                        );
                    },
                    header: () => "Field List",
                }}
            >
                <Route
                    path="edit/:fieldId"
                    element={<Field />}
                    loader={FieldLoader}
                    handle={{
                        crumb: ({ fieldId }: { fieldId: string }) => {
                            return (
                                <Link
                                    to={`/tournament-management/field-list/edit/${fieldId}`}
                                >
                                    Edit Field
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path="add"
                    element={<Field />}
                    loader={FieldLoader}
                    handle={{
                        crumb: () => {
                            return (
                                <Link
                                    to={`/tournament-management/field-list/add`}
                                >
                                    Add Field
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="tournament-registrations"
                element={<TournamentRegistrations />}
                loader={TournamentRegistrationsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link
                                to={`/tournament-management/tournament-registrations`}
                            >
                                Tournament Registrations
                            </Link>
                        );
                    },
                    header: () => "Tournament Registrations",
                }}
            >
                <Route
                    path=":orderId"
                    element={<Order />}
                    loader={OrderLoader}
                    handle={{
                        crumb: ({ orderId }: { orderId: IOrder["id"] }) => {
                            return (
                                <Link
                                    to={`/tournament-management/tournament-registrations/${orderId}`}
                                >
                                    Order #{`${orderId}`}
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                ></Route>
            </Route>
            <Route
                path="game-logs"
                element={<GameLogs />}
                loader={GameLogsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/tournament-management/game-logs`}>
                                Game Logs
                            </Link>
                        );
                    },
                    header: ({ header }: { header: string }) => header,
                }}
            />
            <Route
                path="game-logs/:gameId"
                element={<GameLogs />}
                loader={GameLogsLoader}
                handle={{
                    crumb: ({ gameId }: { gameId: string }) => {
                        return (
                            <Link
                                to={`/tournament-management/game-logs/${gameId}`}
                            >
                                Game Log
                            </Link>
                        );
                    },
                    header: ({ header }: { header: string }) => header,
                }}
            />
            <Route
                path="coiApproval"
                element={<COIApproval />}
                loader={COIApprovalLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/tournament-management/coiApproval`}>
                                COI Approvals
                            </Link>
                        );
                    },
                    header: () => "COI Approvals",
                }}
            >
                <Route
                    path=":teamId"
                    element={<COI />}
                    loader={COILoader}
                    handle={{
                        crumb: ({ teamId }: { teamId: string }) => {
                            return (
                                <Link
                                    to={`/tournament-management/coiApproval/${teamId}`}
                                >
                                    Approve Team COI
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="products"
                element={<ProductsIndex />}
                loader={ProductsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/tournament-management/products`}>
                                Products
                            </Link>
                        );
                    },
                    header: () => "Product Management",
                }}
            >
                <Route
                    path="add"
                    element={<Product />}
                    loader={ProductLoader}
                    handle={{
                        crumb: ({
                            productId,
                        }: {
                            productId: IProduct["id"];
                        }) => {
                            return (
                                <Link
                                    to={`/tournament-management/products/add`}
                                >
                                    Add Product
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path="edit/:productId"
                    element={<Product />}
                    loader={ProductLoader}
                    handle={{
                        crumb: ({
                            productId,
                        }: {
                            productId: IProduct["id"];
                        }) => {
                            return (
                                <Link
                                    to={`/tournament-management/products/edit/${productId}`}
                                >
                                    Edit Product
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="promo-codes"
                element={<PromoCodes />}
                loader={PromoCodesLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/tournament-management/promo-codes`}>
                                Promo Codes
                            </Link>
                        );
                    },
                    header: () => "Promo Code Management",
                }}
            >
                <Route
                    path="add"
                    element={<PromoCode />}
                    loader={PromoCodeLoader}
                    handle={{
                        crumb: ({
                            promoCodeId,
                        }: {
                            promoCodeId: IPromoCode["id"];
                        }) => {
                            return (
                                <Link
                                    to={`/tournament-management/promo-codes/add`}
                                >
                                    Add Promo Code
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path="edit/:promoCodeId"
                    element={<PromoCode />}
                    loader={PromoCodeLoader}
                    handle={{
                        crumb: ({
                            promoCodeId,
                        }: {
                            promoCodeId: IPromoCode["id"];
                        }) => {
                            return (
                                <Link
                                    to={`/tournament-management/promo-codes/edit/${promoCodeId}`}
                                >
                                    Edit Promo Code
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="homeLeagues"
                element={<HomeLeagues />}
                loader={HomeLeaguesLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/tournament-management/homeLeagues`}>
                                Home Leagues
                            </Link>
                        );
                    },
                    header: () => "Home Leagues Management",
                }}
            ></Route>
            <Route
                path="*"
                element={<NotFound parent="tournamentmanagement" />}
                handle={{
                    header: () => "Tournament Management Page Not Found",
                }}
            />
        </Route>
    </Route>
);
